<script setup>
import { reactive, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const ROLE_OPTIONS = [
  {
    value: 'none',
    label: t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.ROLE.OPTIONS.NONE'),
  },
  {
    value: 'handoff',
    label: t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.ROLE.OPTIONS.HANDOFF'),
  },
  {
    value: 'resolution',
    label: t(
      'CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.ROLE.OPTIONS.RESOLUTION'
    ),
  },
];

const state = reactive({
  templates: [],
});

// Stable per-item id for the draggable list's item-key — using the editable
// text itself as the key would recreate the <input> DOM node (and drop
// focus) on every keystroke.
let nextTemplateId = 0;
const generateTemplateId = () => {
  nextTemplateId += 1;
  return nextTemplateId;
};

const addTemplate = () => {
  state.templates.push({
    id: generateTemplateId(),
    name: '',
    role: 'none',
    whenToUse: '',
    message: '',
    enabled: true,
  });
};

const removeTemplate = index => {
  state.templates.splice(index, 1);
};

// Returns this section's partial payload only — never spreads
// props.assistant.config, so the parent page can merge all sections'
// payloads together in one PATCH instead of each section overwriting the
// others' unsaved changes with a stale snapshot of assistant.config.
const getPayload = () => ({
  config: {
    message_templates: state.templates.map(template => ({
      name: template.name,
      role: template.role,
      when_to_use: template.whenToUse,
      message: template.message,
      enabled: template.enabled,
    })),
  },
});

// Compares serialized payloads (not raw state) so the draggable list's
// generated `id` field — which changes every time updateStateFromAssistant
// runs but is never part of what gets saved — doesn't cause a false
// positive dirty state.
let initialPayloadSnapshot = JSON.stringify(getPayload());

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.templates = (config.message_templates || []).map(template => ({
    id: generateTemplateId(),
    name: template.name || '',
    role: template.role || 'none',
    whenToUse: template.when_to_use || '',
    message: template.message || '',
    enabled: template.enabled !== false,
  }));
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

const isDirty = computed(
  () => JSON.stringify(getPayload()) !== initialPayloadSnapshot
);

defineExpose({ getPayload, isDirty });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Draggable
      v-model="state.templates"
      tag="div"
      item-key="id"
      class="flex flex-col gap-3"
      handle=".drag-handle"
    >
      <template #item="{ element: template, index }">
        <div class="flex flex-col gap-3 p-3 border border-n-weak rounded-lg">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-grip-vertical"
              class="drag-handle size-4 text-n-slate-11 cursor-move flex-shrink-0"
            />
            <Switch v-model="template.enabled" />
            <Input
              v-model="template.name"
              class="flex-1"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.NAME_PLACEHOLDER')
              "
            />
            <Button
              icon="i-lucide-x"
              variant="ghost"
              color="slate"
              size="sm"
              @click="removeTemplate(index)"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.ROLE.LABEL') }}
            </label>
            <Select v-model="template.role" :options="ROLE_OPTIONS" />
          </div>
          <Input
            v-model="template.whenToUse"
            :label="
              t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.WHEN_TO_USE.LABEL')
            "
            :placeholder="
              t(
                'CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.WHEN_TO_USE.PLACEHOLDER'
              )
            "
          />
          <TextArea
            v-model="template.message"
            :label="
              t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.MESSAGE.LABEL')
            "
            :max-length="2000"
            show-character-count
            auto-height
            resize
          />
        </div>
      </template>
    </Draggable>

    <Button
      icon="i-lucide-plus"
      variant="ghost"
      size="sm"
      class="!w-fit !text-n-brand hover:!text-n-brand"
      :label="t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.ADD')"
      @click="addTemplate"
    />
  </div>
</template>
